import {
	createMembership,
	deleteMembership,
	fetchMembershipById,
	fetchMembershipByName,
	fetchMemberships,
	updateMembership,
} from "@bodhistreams/api/src/membershipApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * Query keys factory for membership queries
 */
export const membershipQueryKeys = {
	all: ["memberships"],
	lists: () => [...membershipQueryKeys.all, "list"],
	list: () => [...membershipQueryKeys.lists()],
	detail: (id) => [...membershipQueryKeys.all, "detail", id],
	byName: (name) => [...membershipQueryKeys.all, "byName", name],
};

/**
 * Hook to fetch all memberships
 * @param {boolean} enabled - Whether to enable the query
 * @returns {Object} React Query result
 */
export const useMemberships = (enabled = true) => {
	return useQuery({
		queryKey: membershipQueryKeys.list(),
		queryFn: async () => {
			return await fetchMemberships();
		},
		enabled,
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 30 * 60 * 1000, // 30 minutes
	});
};

/**
 * Hook to fetch a membership by ID
 * @param {string|number} id - Membership ID
 * @param {boolean} enabled - Whether to enable the query
 * @returns {Object} React Query result
 */
export const useMembership = (id, enabled = true) => {
	return useQuery({
		queryKey: membershipQueryKeys.detail(id),
		queryFn: async () => {
			return await fetchMembershipById(id);
		},
		enabled: enabled && !!id,
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 30 * 60 * 1000, // 30 minutes
	});
};

/**
 * Hook to fetch memberships by name
 * @param {string} name - Membership name
 * @param {boolean} enabled - Whether to enable the query
 * @returns {Object} React Query result
 */
export const useMembershipByName = (name, enabled = true) => {
	return useQuery({
		queryKey: membershipQueryKeys.byName(name),
		queryFn: async () => {
			return await fetchMembershipByName(name);
		},
		enabled: enabled && !!name,
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 30 * 60 * 1000, // 30 minutes
	});
};

/**
 * Hook to create a new membership (admin only)
 * @returns {Object} React Query mutation result
 */
export const useCreateMembership = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createMembership,
		onSuccess: (newMembership) => {
			// Add to the memberships list cache
			queryClient.setQueryData(membershipQueryKeys.list(), (oldData) => {
				if (!oldData) return [newMembership];
				return [...oldData, newMembership];
			});
			// Set the individual membership cache
			queryClient.setQueryData(
				membershipQueryKeys.detail(newMembership.id),
				newMembership,
			);
		},
	});
};

/**
 * Hook to update a membership (admin only)
 * @returns {Object} React Query mutation result
 */
export const useUpdateMembership = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, membershipData }) =>
			updateMembership(id, membershipData),
		onSuccess: (updatedMembership, { id }) => {
			// Update the individual membership cache
			queryClient.setQueryData(
				membershipQueryKeys.detail(id),
				updatedMembership,
			);
			// Update in the memberships list cache
			queryClient.setQueryData(membershipQueryKeys.list(), (oldData) => {
				if (!oldData) return [updatedMembership];
				return oldData.map((membership) =>
					membership.id === id ? updatedMembership : membership,
				);
			});
		},
	});
};

/**
 * Hook to delete a membership (admin only)
 * @returns {Object} React Query mutation result
 */
export const useDeleteMembership = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: deleteMembership,
		onSuccess: (_, id) => {
			// Remove from cache
			queryClient.removeQueries({
				queryKey: membershipQueryKeys.detail(id),
			});
			// Remove from memberships list cache
			queryClient.setQueryData(membershipQueryKeys.list(), (oldData) => {
				if (!oldData) return [];
				return oldData.filter((membership) => membership.id !== id);
			});
		},
	});
};

/**
 * Hook to get the query client for manual cache operations
 * @returns {Object} React Query client
 */
export const useMembershipQueryClient = () => {
	return useQueryClient();
};

/**
 * Hook to invalidate membership queries
 * @returns {Object} Invalidation functions
 */
export const useInvalidateMemberships = () => {
	const queryClient = useQueryClient();

	return {
		invalidateAll: () =>
			queryClient.invalidateQueries({
				queryKey: membershipQueryKeys.all,
			}),
		invalidateList: () => {
			queryClient.invalidateQueries({
				queryKey: membershipQueryKeys.list(),
			});
		},
		invalidateMembership: (id) => {
			queryClient.invalidateQueries({
				queryKey: membershipQueryKeys.detail(id),
			});
		},
		invalidateByName: (name) => {
			queryClient.invalidateQueries({
				queryKey: membershipQueryKeys.byName(name),
			});
		},
	};
};
